import { useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { FaInfoCircle, FaConciergeBell, FaHeart } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import OverlayLoader from "components/OverlayLoader";

import useCommon from "hooks/useCommon";

import { getReservationPreferences } from "store/slice/reservationSlice";

import { PREFERENCE_QUESTIONS } from "variables/preferences";

const CATEGORY_CONFIG = {
  "About Your Stay": {
    icon: FaInfoCircle,
  },
  "Your Stay Style": {
    icon: FaConciergeBell,
  },
  "Your Preferences": {
    icon: FaHeart,
  },
};

const PreferencesPreviewDialog = ({ open, onClose, hotelId, reservationId }) => {
  const { DISPATCH, STORE } = useCommon();

  const { reservationPreferences, preferencesLoading } = STORE((state) => state.reservation);

  const answers = reservationPreferences?.answers || {};

  useEffect(() => {
    if (!open || !reservationId || !hotelId) return;

    DISPATCH(
      getReservationPreferences({
        hotelId,
        reservationId,
      })
    );
  }, [open, reservationId, hotelId, DISPATCH]);

  const groupedPreferences = useMemo(() => {
    return PREFERENCE_QUESTIONS.reduce((groups, question) => {
      if (!groups[question.category]) {
        groups[question.category] = [];
      }

      groups[question.category].push(question);

      return groups;
    }, {});
  }, []);

  const formatAnswer = (question) => {
    const value = answers[question.questionKey];

    if (value === undefined || value === null || value === "") {
      return "—";
    }

    if (question.type === "time") {
      try {
        return new Date(`1970-01-01T${value}`).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        });
      } catch {
        return value;
      }
    }

    if (typeof value === "object") {
      const selectedValue = Object.entries(value).find(([, isSelected]) => isSelected);

      return selectedValue?.[0] || "—";
    }

    return value;
  };

  const Section = ({ title, icon: Icon, children }) => (
    <VuiBox mb={3}>
      <VuiBox display="flex" alignItems="center" gap={1} mb={1.25} ml={1}>
        <VuiBox
          width={32}
          height={32}
          borderRadius="30%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={({ palette: { light } }) => ({
            background: light.secondary,
          })}
        >
          <Icon size={16} />
        </VuiBox>

        <VuiTypography
          variant="subtitle2"
          fontWeight="regular"
          sx={{
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </VuiTypography>
      </VuiBox>

      <VuiBox
        p={2}
        borderRadius="14px"
        sx={{
          background: `
            linear-gradient(
              135deg,
              rgba(255,255,255,0.05),
              rgba(255,255,255,0.015)
            )
          `,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        {children}
      </VuiBox>
    </VuiBox>
  );

  const Row = ({ label, value, isLast }) => (
    <VuiBox mb={isLast ? 0 : 1.75}>
      <VuiTypography
        variant="body2"
        fontWeight="medium"
        sx={{
          fontSize: "14px",
          mb: 0.5,
        }}
      >
        {label}
      </VuiTypography>

      <VuiTypography
        variant="body2"
        fontWeight="regular"
        sx={{
          fontSize: "14px",
          opacity: 0.7,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {value}
      </VuiTypography>
    </VuiBox>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <OverlayLoader open={preferencesLoading} message="Loading guest preferences..." />

      <DialogTitle>
        <VuiTypography
          fontWeight="medium"
          sx={{
            fontSize: {
              xs: "16px",
              md: "22px",
            },
          }}
        >
          Guest Preferences Overview
        </VuiTypography>

        <VuiTypography variant="caption">Collected via Guest Experience Assistant</VuiTypography>
      </DialogTitle>

      <DialogContent
        sx={{
          p: {
            xs: 1,
            md: 2,
          },
        }}
      >
        {Object.entries(groupedPreferences).map(([category, questions]) => {
          const categoryConfig = CATEGORY_CONFIG[category];
          const Icon = categoryConfig?.icon || FaInfoCircle;

          return (
            <Section key={category} title={category} icon={Icon}>
              {questions.map((question, index) => (
                <Row
                  key={question.questionKey}
                  label={question.description}
                  value={formatAnswer(question)}
                  isLast={index === questions.length - 1}
                />
              ))}
            </Section>
          );
        })}
      </DialogContent>

      <DialogActions>
        <VuiButton variant="outlined" color="primary" onClick={onClose}>
          Close
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default PreferencesPreviewDialog;
